/**
 * Saved estimates
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardActionArea, Chip, Stack, Typography } from '@mui/material';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { EmptyState, IconTile, Page, PageHeader } from '../ui';
import HouseIllustration from '../estimate/HouseIllustration';
import { describeInputs } from '../estimate/EstimateResultView';
import { getEstimateCopy } from '../estimate/copy';
import { useLanguage } from '../../utils/LanguageContext';
import { getProjects } from '../../utils/projectStorage';
import { formatCurrency, formatDate } from '../../utils/format';

const text = {
  en: {
    title: 'Projects',
    subtitle: 'Estimates you have saved on this device.',
    emptyTitle: 'No saved projects yet',
    emptyDesc: 'Save an estimate to keep it here, open it later, compare or export a PDF.',
    start: 'Start an estimate',
    newEstimate: 'New estimate',
    saved: 'Saved',
  },
  my: {
    title: 'Projek',
    subtitle: 'Anggaran yang anda simpan pada peranti ini.',
    emptyTitle: 'Belum ada projek disimpan',
    emptyDesc: 'Simpan anggaran untuk menyimpannya di sini, buka semula, bandingkan atau eksport PDF.',
    start: 'Mula anggaran',
    newEstimate: 'Anggaran baru',
    saved: 'Disimpan',
  },
};

export const ProjectRow = ({ project, onClick, language }) => {
  const t = getEstimateCopy(language);
  return (
    <Card>
      <CardActionArea onClick={onClick} sx={{ p: { xs: 1.75, sm: 2 } }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <IconTile
            size={52}
            icon={
              <Box sx={{ width: 40, height: 26, '& svg': { fontSize: 'unset' } }}>
                <HouseIllustration type={project.inputs.houseType || 'terrace'} />
              </Box>
            }
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap>
              {project.inputs.projectName || t.resultTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {describeInputs(project.inputs, t, language)}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
              <Typography variant="subtitle2" className="tabular">
                {formatCurrency(project.summary.totalCost.avg)}
              </Typography>
              <Chip size="small" variant="outlined" label={project.mode === 'quick' ? t.quickBadge : t.detailedBadge} />
            </Stack>
          </Box>
          <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
            <ChevronRightRoundedIcon color="action" />
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {formatDate(project.savedAt, language)}
            </Typography>
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  );
};

const ProjectsPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = text[language] || text.en;
  const [projects] = useState(getProjects);

  return (
    <Page>
      <PageHeader
        title={t.title}
        subtitle={t.subtitle}
        actions={
          projects.length > 0 && (
            <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => navigate('/app/estimate')}>
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                {t.newEstimate}
              </Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                {language === 'my' ? 'Baru' : 'New'}
              </Box>
            </Button>
          )
        }
      />

      {projects.length === 0 ? (
        <Card>
          <EmptyState
            icon={<FolderOpenOutlinedIcon />}
            title={t.emptyTitle}
            description={t.emptyDesc}
            action={
              <Button variant="contained" onClick={() => navigate('/app/estimate')}>
                {t.start}
              </Button>
            }
          />
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {projects.map((p) => (
            <ProjectRow key={p.id} project={p} language={language} onClick={() => navigate(`/app/projects/${p.id}`)} />
          ))}
        </Stack>
      )}
    </Page>
  );
};

export default ProjectsPage;
